import React from "react";
import styled from "styled-components";

import DateDisplay, { DATE_DISPLAY_TYPE } from "../date-display/DateDisplay";
import DropdownMenu, {
  type DropdownMenuItem,
  type DropdownMenuProps,
} from "../dropdown-menu/DropdownMenu";
import UserAvatar from "../avatar/UserAvatar";
import {
  useMessageReadReceipts,
  type MessageReadReceiptUser,
} from "./useMessageReadReceipts";

// アクションエリアラッパー（行全体の高さに広げる）
const ActionsWrapper = styled.div`
  align-self: stretch;
  display: flex;
`;

// アクションエリア内部。縦並びで上段スロット / 中央メニュー / 下段メタを
// space-between で配置する。狭幅時（Container Query）は横並びに切り替える。
const ActionsInner = styled.div<{ $isOurs?: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: ${(props) => (props.$isOurs ? "flex-end" : "flex-start")};
  height: 100%;

  @container (max-height: 60px) {
    flex-direction: ${(props) => (props.$isOurs ? "row" : "row-reverse")};
    align-items: center;
  }
`;

// 3 点メニューコンテナ。親行コンテナの `&:hover .message-actions` で表示制御する。
const MessageActionsContainer = styled.div`
  display: flex;
  align-items: center;
  visibility: hidden;
  opacity: 0;

  &.message-actions {
    position: relative;
  }
`;

// メタブロック（日時 + 既読アイコンの縦並び）
const MetaBlock = styled.div<{ $isOurs?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${(props) => (props.$isOurs ? "flex-end" : "flex-start")};
  gap: 2px;

  @container (max-height: 60px) {
    align-self: flex-end;
  }
`;

const MessageTimestamp = styled.span.attrs({ className: "label8" })`
  color: var(--soremo-light-gray);
  white-space: nowrap;
`;

// 既読アバターコンテナ（重ね表示）
const ReadReceiptsContainer = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  max-width: 80px;

  & > * {
    margin-left: -4px;

    &:first-child {
      margin-left: 0;
    }
  }

  & > * > div {
    box-shadow: none;
  }
`;

// 既読ステータスコンテナ（チェックマーク + 日時）
const ReadStatusContainer = styled.div`
  display: flex;
  align-items: center;
`;

// 既読チェックマーク
const ReadCheckmark = styled.span.attrs({
  className: "material-symbols-rounded",
})`
  color: var(--soremo-light-gray);
  font-size: 12px;
  line-height: 1;
`;

export interface MessageMetaColumnProps {
  isOurs: boolean;
  /** 作成日時（ISO 8601）。既読日時が無いときに表示する。 */
  createdAt: string;
  /** 受信者一覧（既読アイコン / 既読日時の算出に使用）。 */
  receivers?: MessageReadReceiptUser[];
  /** 既読フィルタ用の現在ユーザー ID。 */
  currentUserId?: number | string;
  /** 3 点メニューの項目。空または未指定ならメニュー枠を出さない。 */
  menuItems?: DropdownMenuItem[];
  /** メニューを表示するか（hover で出現）。既定は menuItems があれば true。 */
  showMenu?: boolean;
  /** メニューの表示位置。 */
  menuPlacement?: DropdownMenuProps["placement"];
  /** 上段スロット（完了アイコン等）。未指定なら空のスペーサー。 */
  topSlot?: React.ReactNode;
}

/**
 * メッセージ右側のメタ列（縦並び）。
 *
 * - 上段: 任意スロット（完了アイコン等）
 * - 中央: 3 点リーダーメニュー（hover で表示）
 * - 下段: 作成日 or 既読日時 + 既読アイコン + 既読アバター
 *
 * 通常メッセージ（`MessageItem`）と契約書アイテム（`ContractTimelineItem`）を
 * 同一構成にするための共通コンポーネント。hover 表示は親行コンテナ側の
 * `container-type: inline-size;` と
 * `&:hover .message-actions { visibility: visible; opacity: 1; }` に依存する。
 */
const MessageMetaColumn: React.FC<MessageMetaColumnProps> = ({
  isOurs,
  createdAt,
  receivers,
  currentUserId,
  menuItems,
  showMenu,
  menuPlacement,
  topSlot,
}) => {
  const { readReceiptUsers, latestSeenAt } = useMessageReadReceipts(
    receivers,
    isOurs,
    currentUserId,
  );

  const hasMenuItems = menuItems != null && menuItems.length > 0;
  const renderMenu = (showMenu ?? hasMenuItems) && hasMenuItems;

  return (
    <ActionsWrapper>
      <ActionsInner $isOurs={isOurs}>
        {topSlot ?? <div />}
        {renderMenu && (
          <MessageActionsContainer className="message-actions">
            <DropdownMenu items={menuItems!} placement={menuPlacement} />
          </MessageActionsContainer>
        )}
        <MetaBlock $isOurs={isOurs}>
          {isOurs && latestSeenAt ? (
            <ReadStatusContainer>
              <ReadCheckmark>done</ReadCheckmark>
              <MessageTimestamp>
                <DateDisplay
                  date={latestSeenAt}
                  type={DATE_DISPLAY_TYPE.ADAPTIVE}
                />
              </MessageTimestamp>
            </ReadStatusContainer>
          ) : (
            <MessageTimestamp>
              <DateDisplay
                date={createdAt}
                type={DATE_DISPLAY_TYPE.ADAPTIVE}
              />
            </MessageTimestamp>
          )}
          {readReceiptUsers.length > 0 && (
            <ReadReceiptsContainer>
              {readReceiptUsers.map((user) => (
                <UserAvatar
                  key={user.id}
                  userId={user.id}
                  avatar={user.avatar}
                  displayName={user.display_name}
                  size={12}
                />
              ))}
            </ReadReceiptsContainer>
          )}
        </MetaBlock>
      </ActionsInner>
    </ActionsWrapper>
  );
};

export default MessageMetaColumn;
